import type { CallInfo } from '@compass/client/types/call';
import { CallStatus, CallType } from '@compass/client/types/call';
import { AEPQC_PREPROCESSING_WORKFLOW_TYPE } from '@constants/dispositions';

/**
 * True when a call's case has already been created — i.e. the case has moved
 * past the aepqc_preprocessing draft (link-to-case promoted it to pqc). Derived
 * from the server-provided live `case_workflow_type` (resolved at read time in
 * GetMyUserCalls), NOT client localStorage, so it stays correct across
 * browsers/devices/sessions.
 *
 * An empty `case_workflow_type` (no case, or a list endpoint that didn't resolve
 * it) returns false, so the UI still offers "Create Case".
 */
export const isCaseLinked = (call: Pick<CallInfo, 'case_workflow_type'>): boolean =>
  !!call.case_workflow_type && call.case_workflow_type !== AEPQC_PREPROCESSING_WORKFLOW_TYPE;

/**
 * Convert string status to CallStatus enum value
 * Handles both numeric enum values and string representations (e.g., 'CALL_STATUS_COMPLETED')
 */
const parseCallStatus = (status: string): CallStatus | undefined => {
  // Check if it's a valid enum key name
  const enumValue = CallStatus[status as keyof typeof CallStatus];
  if (typeof enumValue === 'number') {
    return enumValue;
  }
  return undefined;
};

/**
 * Convert string type to CallType enum value
 * Handles both numeric enum values and string representations (e.g., 'CALL_TYPE_HUMAN')
 */
const parseCallType = (type: string): CallType | undefined => {
  // Check if it's a valid enum key name
  const enumValue = CallType[type as keyof typeof CallType];
  if (typeof enumValue === 'number') {
    return enumValue;
  }
  return undefined;
};

export const getCallStatusDisplay = (status?: CallStatus | string): string => {
  // If status is a string, try to convert it to enum value
  const normalizedStatus = typeof status === 'string' ? parseCallStatus(status) : status;

  switch (normalizedStatus) {
    case CallStatus.CALL_STATUS_COMPLETED:
      return 'Completed';
    case CallStatus.CALL_STATUS_IN_PROGRESS:
      return 'In Progress';
    case CallStatus.CALL_STATUS_SCHEDULED:
      return 'Scheduled';
    case CallStatus.CALL_STATUS_FAILED:
      return 'Failed';
    case CallStatus.CALL_STATUS_CANCELLED:
      return 'Cancelled';
    case CallStatus.CALL_STATUS_NOT_STARTED:
      return 'Not Started';
    default:
      return 'Unspecified';
  }
};

export const getCallDuration = (startedAt?: string, endedAt?: string): string => {
  if (startedAt && endedAt) {
    const start = new Date(startedAt);
    const end = new Date(endedAt);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return '-';
    }

    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  return '-';
};

export const formatCallType = (type?: CallType | string): string => {
  // If type is a string, try to convert it to enum value
  const normalizedType = typeof type === 'string' ? parseCallType(type) : type;

  switch (normalizedType) {
    case CallType.CALL_TYPE_HUMAN:
      return 'Human Call';
    case CallType.CALL_TYPE_AI:
      return 'AI Call';
    case CallType.CALL_TYPE_EXTERNAL:
      return 'External Call';
    default:
      return 'Unspecified'; // Default for CALL_TYPE_UNSPECIFIED or undefined
  }
};
